import React, { ReactElement } from 'react';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Hint from '../../../../components/hint/hint.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldPassword from '../../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../../services/format-service';
import AzureDatabaseService from '../../../../services/azure-database-service';
import { ACCOUNT_USER_TYPE_SYSTEM_ADMIN } from '../../../../constants/entity/account-user-types';

const SettingsAccountAzure: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    currentUser,
    accountData,
    onCancelForm,
    onSubmitForm,
  } = props;

  const formPrefix: string = 'account';
  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;
  const azureSqlServerPortDefault: string = AzureDatabaseService.AZURE_DATABASE_PORT_DEFAULT.toString();
  const azureSqlServerDatabaseSchemaDefault: string = AzureDatabaseService.AZURE_DATABASE_SCHEMA_DEFAULT;

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Azure Enabled
              {
                !accountData.azureIsEnabled && !isCurrentUserSystemAdmin && (
                  <Hint
                    title={'You cannot enable Azure in your account manually. Please contact your administrator.'}
                  />
                )
              }
              {
                accountData.azureIsEnabled && (
                  <Hint title={'Once Azure is enabled, configuration from the fields below is in use.'} />
                )
              }
            </TableCell>
            <TableCell>
              {
                (!isEditMode || !isCurrentUserSystemAdmin) && (
                  FormatService.formatBooleanValue(accountData.azureIsEnabled)
                )
              }
              {
                isEditMode && isCurrentUserSystemAdmin && (
                  <FormFieldCheckbox
                    withNoOffset
                    withNoLabelOffset
                    name={`${formPrefix}.azureIsEnabled`}
                  />
                )
              }
            </TableCell>
          </TableRow>
          {
            accountData.azureIsEnabled && (accountData.azureBlobStorageAccountName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure Blob Storage Account Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureBlobStorageAccountName`} />
                    ) : (
                      accountData.azureBlobStorageAccountName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureBlobStorageAccountKey || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure Blob Storage Account Key
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldPassword
                        isBordered
                        withNoAutocomplete
                        name={`${formPrefix}.azureBlobStorageAccountKey`}
                      />
                    ) : ((!!accountData.azureBlobStorageAccountKey
                      && FormatService.formatEncryptedString(accountData.azureBlobStorageAccountKey)
                    ) || '')
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureBlobStorageContainerName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure Blob Storage Container Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureBlobStorageContainerName`} />
                    ) : (
                      accountData.azureBlobStorageContainerName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerAccountName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Account Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureSqlServerAccountName`} />
                    ) : (
                      accountData.azureSqlServerAccountName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerPort || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Port (optional)
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        name={`${formPrefix}.azureSqlServerPort`}
                        placeholder={azureSqlServerPortDefault}
                      />
                    ) : (
                      accountData.azureSqlServerPort
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerUserName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server User Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureSqlServerUserName`} />
                    ) : (
                      accountData.azureSqlServerUserName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerPassword || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Password
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldPassword isBordered withNoAutocomplete name={`${formPrefix}.azureSqlServerPassword`} />
                    ) : ((!!accountData.azureSqlServerPassword
                      && FormatService.formatEncryptedString(accountData.azureSqlServerPassword)
                    ) || '')
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerDatabaseName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Database Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureSqlServerDatabaseName`} />
                    ) : (
                      accountData.azureSqlServerDatabaseName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerDatabaseSchema || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Database Schema (optional)
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        name={`${formPrefix}.azureSqlServerDatabaseSchema`}
                        placeholder={azureSqlServerDatabaseSchemaDefault}
                      />
                    ) : (
                      accountData.azureSqlServerDatabaseSchema
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerDatasourceName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Datasource Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureSqlServerDatasourceName`} />
                    ) : (
                      accountData.azureSqlServerDatasourceName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.azureIsEnabled && (accountData.azureSqlServerStoredProcedureRefreshName || isEditMode) && (
              <TableRow>
                <TableCell>
                  Azure SQL Server Stored Procedure Refresh Name
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.azureSqlServerStoredProcedureRefreshName`} />
                    ) : (
                      accountData.azureSqlServerStoredProcedureRefreshName
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default SettingsAccountAzure;
